import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { getUsersToVerify, approveUser, rejectUser } from '../../../utils/adminFunctions';
import { designSystem } from '../../../styles/design-system';

const VerificationCard = styled(motion.div)`
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};
  padding: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${designSystem.shadows.card};
  transition: all ${designSystem.transitions.normal};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${designSystem.shadows.hover};
  }
`;

const UserInfo = styled.div`
  h4 {
    margin: 0 0 ${designSystem.spacing.sm} 0;
    color: ${designSystem.colors.text.primary};
  }
  p {
    margin: 0;
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${designSystem.spacing.md};
`;

const ActionButton = styled.button<{ a_type: 'approve' | 'reject' }>`
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  border: none;
  border-radius: ${designSystem.borderRadius.md};
  cursor: pointer;
  font-weight: ${designSystem.typography.fontWeight.semibold};
  transition: all ${designSystem.transitions.normal};
  background-color: ${props => props.a_type === 'approve' ? designSystem.colors.status.success : designSystem.colors.status.error};
  color: white;

  &:hover {
    opacity: 0.9;
    transform: scale(1.05);
  }
`;

interface VerificationPanelProps {
  userId?: string | null;
  onClose?: () => void;
  onVerificationComplete?: () => void;
}

const VerificationPanel: React.FC<VerificationPanelProps> = ({ userId, onClose, onVerificationComplete }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const usersToVerify = await getUsersToVerify();
      setUsers(usersToVerify);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApprove = async (userId: string) => {
    try {
      await approveUser(userId);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleReject = async (userId: string) => {
    try {
      await rejectUser(userId);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <p>Caricamento utenti da verificare...</p>;
  if (error) return <p style={{ color: 'red' }}>Errore: {error}</p>;

  return (
    <div>
      <h3>Utenti in Attesa di Verifica</h3>
      {users.length === 0 ? (
        <p>Nessun utente da verificare al momento.</p>
      ) : (
        users.map(user => (
          <VerificationCard key={user.id} layout>
            <UserInfo>
              <h4>{user.full_name || user.email}</h4>
              <p>Registrato il: {new Date(user.created_at).toLocaleDateString()}</p>
            </UserInfo>
            <Actions>
              <ActionButton a_type="approve" onClick={() => handleApprove(user.id)}>Approva</ActionButton>
              <ActionButton a_type="reject" onClick={() => handleReject(user.id)}>Rifiuta</ActionButton>
            </Actions>
          </VerificationCard>
        ))
      )}
    </div>
  );
};

export default VerificationPanel;
